/**
 * Version-check decision core (installed-PWA auto-update).
 *
 * Pure so the "detect → hold-during-session → apply-when-idle" policy is fully
 * unit-testable without a DOM. The client component (VersionAutoRefresh) owns
 * only the plumbing: fetch `/api/version`, read {@link isSessionActive}, and
 * honor the returned effect.
 *
 * The decision is deliberately STATELESS against a server-injected baseline
 * (codex 리뷰 반영):
 *   - `baseline` is the build version of the CURRENTLY LOADED page, rendered
 *     into the HTML by app/layout.tsx from the same source as /api/version
 *     (codex 주요 2) — so "am I stale?" needs no client-remembered first fetch,
 *     and a deploy that happens while the very first fetch fails is still
 *     caught by the next successful one.
 *   - Reload fires ONLY on a CONFIRMED fresh observation: a null fetch
 *     (offline / error) never reloads, even if an earlier check saw a newer
 *     version (codex 주요 1 — with no service worker, reloading offline would
 *     swap a working screen for an error page). The "held during a session"
 *     update is simply re-observed by the next successful check after the
 *     session ends.
 *   - A rollback (or a late/stale response equal to the baseline) is inherently
 *     a no-op: observed == baseline ⇒ nothing pending (codex 보통 3).
 */
export type UpdateEffect = "none" | "reload";

export function decideVersionCheck(input: {
  /** Build version of the loaded page (server-injected at render time). */
  baseline: string;
  /** This check's freshly fetched version, or null on network failure. */
  fetchedVersion: string | null;
  /** True while a live coaching session must not be interrupted. */
  sessionActive: boolean;
}): UpdateEffect {
  const { baseline, fetchedVersion, sessionActive } = input;
  // codex 주요 1: no confirmed observation → never reload (offline-safe).
  if (!fetchedVersion) return "none";
  // codex 보통 3: matching the loaded build (incl. rollback) clears any hold.
  if (fetchedVersion === baseline) return "none";
  // A different deployed build: apply when idle, hold during a live session.
  return sessionActive ? "none" : "reload";
}

/**
 * Wrap an async task so concurrent triggers collapse into one run (codex 보통
 * 4): while a check is in flight, further calls are skipped — mount,
 * visibilitychange, and the poll interval can all fire together without racing
 * (a late response can't act on a stale view of the world, because only one
 * check observes-and-decides at a time).
 */
export function createSingleFlight(
  run: () => Promise<void>,
): () => Promise<void> {
  let inFlight = false;
  return async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      await run();
    } finally {
      inFlight = false;
    }
  };
}
